import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Mic, Sparkles } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { VoiceRecorder } from "@/components/VoiceRecorder";
import { api, errText } from "@/lib/api";
import { shortDate } from "@/lib/eventMeta";

/** Dictate what happened at a hearing — transcribed, tidied and filed on the entry. */
export const VoiceLogDialog = ({ open, onOpenChange, event, onSaved }) => {
  const [busy, setBusy] = useState(false);
  const [existing, setExisting] = useState([]);
  const [mode, setMode] = useState("append");

  const check = useCallback(async () => {
    if (!event?.event_id) return;
    try {
      const { data } = await api.get("/logs", { params: { event_id: event.event_id } });
      let found = data;
      if (!found.length && event.matter_id) {
        const { data: sameDay } = await api.get("/logs", {
          params: { matter_id: event.matter_id, date: event.date },
        });
        found = sameDay;
      }
      setExisting(found);
      setMode(found.length ? "append" : "new");
    } catch {
      setExisting([]);
      setMode("new");
    }
  }, [event?.event_id, event?.matter_id, event?.date]);

  useEffect(() => {
    if (open) check();
  }, [open, check]);

  const send = async (file) => {
    setBusy(true);
    try {
      const body = new FormData();
      body.append("file", file);
      if (event?.event_id) body.append("event_id", event.event_id);
      if (event?.matter_id) body.append("matter_id", event.matter_id);
      if (event?.date) body.append("date", event.date);
      body.append("mode", existing.length ? mode : "new");
      const { data } = await api.post("/logs/voice", body);
      onOpenChange(false);
      onSaved?.(data);
      if (data?.pending_voice) {
        toast.success("Recording saved on this device", {
          description: "It will be written up onto the hearing log as soon as you're back online.",
          duration: 9000,
        });
        return;
      }
      toast.success("Recording filed on this hearing", {
        description: data.appended
          ? "It is being written up and added to the existing log — carry on, you'll see it in a moment."
          : "It is being written up in the background — carry on, you'll see it in a moment.",
        duration: 8000,
      });
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-xl max-h-[75vh] overflow-y-auto" data-testid="voice-log-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Record voice log</DialogTitle>
          <DialogDescription className="text-sm">
            Say what happened — the order, the directions, what is to be done and the next date.
            English, Hindi or a mix. Stop recording and you're done: this closes at once and the note
            is written up in the background, then waits on your dashboard for a read.
          </DialogDescription>
        </DialogHeader>

        <div className="py-2 space-y-4">
          <div className="border border-border rounded-sm px-4 py-3" data-testid="voice-log-target">
            <p className="label-caps">This hearing</p>
            <p className="mt-1 text-base font-semibold leading-snug">{event?.title}</p>
            <p className="text-sm text-muted-foreground">
              {[event?.date ? shortDate(event.date) : null, event?.court_name, event?.case_number]
                .filter(Boolean)
                .join(" · ")}
            </p>
          </div>

          {existing.length ? (
            <div className="space-y-2" data-testid="voice-log-mode">
              <p className="label-caps">
                {existing.length} log already on this hearing — where should this go?
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {[
                  ["append", "Add to that log", "Kept as one note for the hearing"],
                  ["new", "Start a new log", "Filed as a separate entry"],
                ].map(([value, label, hint]) => (
                  <button
                    key={value}
                    type="button"
                    data-testid={`voice-log-mode-${value}`}
                    onClick={() => setMode(value)}
                    className={`px-4 py-3 rounded-sm border text-left transition-colors duration-200 ${
                      mode === value
                        ? "bg-primary text-primary-foreground border-primary"
                        : "border-border hover:bg-accent"
                    }`}
                  >
                    <span className="block text-sm font-semibold">{label}</span>
                    <span className="block text-xs opacity-80">{hint}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : null}

          <VoiceRecorder onAudio={send} busy={busy} />
          {busy ? (
            <p className="inline-flex items-center gap-2 text-sm text-muted-foreground">
              <Sparkles className="h-4 w-4 animate-pulse" /> Filing it on the hearing…
            </p>
          ) : (
            <p className="inline-flex items-center gap-2 text-xs text-muted-foreground">
              <Mic className="h-3.5 w-3.5" /> Every point you speak is kept — only the grammar and
              punctuation are tidied.
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
